import React from 'react';
import { AccountEntity } from '@interpals/api-web';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import ROUTES, { makeGroupSearchURL, makeProfileURL } from 'routes';
import { useAppDispatch } from 'hooks';
import { logout } from 'redux/auth/actions';
import Icon from 'components/Icon';
import UserAvatar from 'components/UserAvatar';
import Popover from 'components/ui/Popover';
import { SpriteIcon } from 'types/icons';

import AccountMenuButton from './AccountMenuButton';

import css from './AccountMenu.module.scss';

interface Props {
    account: AccountEntity;
}

interface MenuItem {
    onClick: () => void;
    content?: SpriteIcon;
    text: string;
}

const AccountMenu: React.FC<Props> = ({ account }) => {
    const { t } = useTranslation();

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const accountMenuItems: MenuItem[] = [
        { onClick: () => navigate(makeProfileURL(account.username)), text: t('AccountMenu.MyPage') },
        { onClick: () => navigate(ROUTES.FRIENDS), content: 'user', text: t('AccountMenu.MyFriends') },
        { onClick: () => navigate(makeGroupSearchURL()), content: 'users-three', text: t('AccountMenu.Groups') },
        { onClick: () => navigate(ROUTES.BOOKMARKS), content: 'bookmark', text: t('AccountMenu.Bookmarks') },
        { onClick: () => navigate(ROUTES.SETTINGS), content: 'settings-gear', text: t('Common.Sections.Settings') },
        {
            onClick: () => navigate(ROUTES.SUPPORT),
            content: 'support-lifesaver',
            text: t('Common.Sections.Support'),
        },
        {
            onClick: () => {
                dispatch(logout());
                navigate(ROUTES.LOGIN);
            },
            content: 'logout',
            text: t('AccountMenu.Logout'),
        },
    ];

    return (
        <Popover
            padding="S"
            className={css.triangle}
            offsetHorizontal={-55}
            offsetVertical={-6}
            content={
                <Popover.Menu>
                    {accountMenuItems.map((item, index) => (
                        <Popover.MenuItem
                            className={css.item}
                            key={index}
                            onClick={item.onClick}
                            beforeContent={
                                !item.content ? (
                                    <UserAvatar user={account} size="XXS" withFlag={false} />
                                ) : (
                                    <Icon name={item.content} color="gray3" width={16} />
                                )
                            }
                        >
                            {item.text}
                        </Popover.MenuItem>
                    ))}
                </Popover.Menu>
            }
        >
            <AccountMenuButton account={account} />
        </Popover>
    );
};

export default React.memo(AccountMenu);
